import Radium from '@flipdish/radium';
import axios from 'axios';
import * as Raven from 'raven-js';
import React from 'react';
import { Form, FormGroup, Well } from 'react-bootstrap';
import { findDOMNode } from 'react-dom';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { hashHistory, Link } from 'react-router';
import {
  Button,
  Container,
  Grid,
  Hidden,
  Icon,
  PageTitle,
  SectionHeader,
  SectionHeaderIcon,
  SectionHeaderText,
  Typography
} from '../../../ui';
import * as Actions from '../../actions/Actions';
import { Track } from '../../analytics';
import * as Api from '../../Api';
import * as Data from '../../Data';
import * as Store from '../../Store';
import { Utilities } from '../../Utilities';
import PhoneCallButton from './components/PhoneCallButton';
import cookie from 'cookie_js';
import RememberMe from '../../../ui/RememberMe/RememberMe';
import * as cookieConsentActions from '../../redux/cookie-consent/actions';
import * as selectors from '../../selectors/cookieSelector';
import { cookieConsent } from '../../cookieconsent';
import { indexOf } from 'lodash';
import { MainLayout } from '../MainLayout/MainLayout';

const tinycolor = require('tinycolor2');

// Injected by the router.
interface RouterParams {
  params: {
    phone: string;
    dlid?: number;
  };
}

interface StateProps {
  DeviceId: string;
  AfterLogin: string;
  translate: any;
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
  AppConfig: Data.Async<Api.AppConfig>;
  rememberMe: boolean;
  isFunctionalCookieConsentEnabled: boolean;
  hasGdprCookieFeatureFlag: boolean;
  enableRememberMe: boolean;
}

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;

type Props = StateProps & MappedDispatch & RouterParams;

interface State {
  Submitting: boolean;
  SmsCode: string;
  ErrorMessage: string;
  callRequested: boolean;
}

class SmsCode extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);
    this.state = {
      SmsCode: '',
      Submitting: false,
      ErrorMessage: '',
      callRequested: false
    };
  }

  public componentDidMount() {
    Utilities.ensureVisible(findDOMNode(this));
  }

  private backLink = (): string => {
    if (this.props.params.dlid) {
      return '/phone/' + this.props.params.dlid;
    } else {
      return '/phone';
    }
  };

  private trackChangePhone = (): any => {
    Track('Login', 'Change Phone');
  };

  private setAuthCookie = (flipdishToken: string): any => {
    if (!flipdishToken) return;

    const { rememberMe, isFunctionalCookieConsentEnabled, hasGdprCookieFeatureFlag } = this.props;
    const date = new Date();
    date.setMonth(date.getMonth() + 6); // Expires in 6 months

    const shouldPreserveAuthCookie =
      (hasGdprCookieFeatureFlag && (isFunctionalCookieConsentEnabled || rememberMe)) ||
      !hasGdprCookieFeatureFlag;

    cookie.set('flipdish-token', flipdishToken, {
      expires: shouldPreserveAuthCookie ? date : undefined
    });
  };

  private handleSmsCodeSubmit = () => {
    this.setState({
      Submitting: true,
      ErrorMessage: ''
    });

    interface Params {
      deviceId: string;
      SmsCode: string;
      PhoneNumber: string;
      selectedDeviceDeliveryLocationId: number | undefined;
      rememberMe?: boolean;
    }

    const { setRememberMe, rememberMe, hasGdprCookieFeatureFlag, enableRememberMe } = this.props;

    let params: Params = {
      deviceId: this.props.DeviceId,
      SmsCode: this.state.SmsCode,
      PhoneNumber: this.props.params.phone,
      selectedDeviceDeliveryLocationId: this.props.params.dlid
    };

    if (hasGdprCookieFeatureFlag) {
      params = { ...params, rememberMe };
    }

    axios
      .post('/Account/LoginUsingPhoneNumber/', params)
      .then((result) => {
        console.log('login result', result);

        const flipdishToken = result.headers['x-flipdish-token'];
        this.setAuthCookie(flipdishToken);

        if (flipdishToken) {
          axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
        }

        if (enableRememberMe && rememberMe) {
          // Enable functional cookies when remember me checked!
          const selected = cookieConsent.getConsentCookies();
          if (selected && indexOf(selected, 'functional') === -1) {
            selected.push('functional');
            cookieConsent.setConsentCookies(selected);
          }
          setRememberMe(false);
        }

        const r: Api.Result<any> = result.data;

        if (r.Success) {
          this.props.Login();
          hashHistory.replace(this.props.AfterLogin);
          Track('Login', 'SMS Code Entry Successful');
        } else {
          this.setState({
            ErrorMessage: r.UserMessage,
            Submitting: false,
            SmsCode: ''
          });
          Track('Login', 'SMS Code Entry Failed');
        }
      })
      .catch((e) => {
        console.error('login fail', e);
        alert(this.props.translate('There_was_an_error_logging_in'));

        const { AppConfig } = this.props;

        let appconfig: any = {};

        if (AppConfig.status === 'success') {
          appconfig.AppNameId = AppConfig.data.AppNameId;
          appconfig.WhiteLabelId = AppConfig.data.WhiteLabelId;
        }

        Raven.captureMessage('Error with SMS code submission', {
          tags: { category: 'API failure' },
          extra: {
            error: Utilities.handleError(e),
            appconfig: appconfig,
            phone: this.props.params.phone,
            smscode: this.state.SmsCode
          }
        });
        Track('Login', 'SMS Code Entry Failed');
      });

    Track('Login', 'Submit SMS Code Button');
  };

  private handleSmsNumberChange = (event: any) => {
    // TODO: type
    this.setState(
      {
        SmsCode: event.target.value,
        Submitting: this.state.Submitting,
        ErrorMessage: this.state.ErrorMessage
      },
      () => {
        if (this.state.SmsCode.length == 4) {
          this.handleSmsCodeSubmit();
        }
      }
    );
  };

  public render(): JSX.Element {
    const {
      translate,
      AppDesignScheme,
      isDark,
      rememberMe,
      setRememberMe,
      enableRememberMe
    } = this.props;

    let errorMessage: JSX.Element | null = null;
    if (this.state.ErrorMessage) {
      errorMessage = (
        <Well className="bg-danger">
          <p aria-live="polite" tabIndex={0} className="clear-margins danger fd-h3">
            {this.state.ErrorMessage}
          </p>
        </Well>
      );
    }

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      secondary: {
        color: AppDesignScheme.Secondary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      },
      subheader: {
        borderColor: AppDesignScheme.Secondary,
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        backgroundColor: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString()
      },
      darkSubheader: {
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
        backgroundColor: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary).darken(10)}`
      },
      nav: {
        ':hover': {},
        ':active': {}
      },
      secondaryActive: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      secondaryHover: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {}
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      buttonHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      formControl: {
        color: AppDesignScheme.Tertiary,
        background: AppDesignScheme.Primary,
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .setAlpha(0.2)
          .toString(),
        ':focus': {
          borderColor: AppDesignScheme.Secondary
        }
      },
      darkFormControl: {
        color: AppDesignScheme.Primary,
        background: AppDesignScheme.Tertiary,
        borderColor: AppDesignScheme.Primary
      },
      btnDisplaySubmit: {
        display: 'inline',
        marginRight: 20
      }
    };

    return (
      <MainLayout>
        <Container maxWidth="lg" className="phone">
          <PageTitle>{translate('Login')}</PageTitle>

          <Link to={this.backLink()} onClick={this.trackChangePhone} data-fd="sms-back">
            <div key="fd-nav" className="fd-nav flex flex-align-center" style={styles.nav as any}>
              <i
                className="arrow_carrot-left tertiary"
                aria-hidden="true"
                style={
                  Radium.getState(this.state, 'fd-nav', ':active')
                    ? styles.secondaryActive
                    : Radium.getState(this.state, 'fd-nav', ':hover')
                    ? styles.secondaryHover
                    : isDark
                    ? styles.primary
                    : styles.tertiary
                }
              />
              <p
                className="inline-block clear-margins tertiary bold"
                style={
                  Radium.getState(this.state, 'fd-nav', ':active')
                    ? styles.secondaryActive
                    : Radium.getState(this.state, 'fd-nav', ':hover')
                    ? styles.secondaryHover
                    : isDark
                    ? styles.primary
                    : styles.tertiary
                }
              >
                <u>{translate('Back')}</u>
              </p>
            </div>
          </Link>

          <SectionHeader>
            <SectionHeaderIcon>
              <Icon fontSize="large" aria-hidden color="inherit" className="ei icon_mobile" />
            </SectionHeaderIcon>
            <SectionHeaderText title={translate('Enter_sms_code')} />
          </SectionHeader>

          <Typography variant="h4" color="textPrimary" paragraph>
            {translate('Sms_verify')}
          </Typography>

          <div className="phone__number flex flex-justify-center flex-align-center">
            <p
              className="tertiary clear-margins bold fd-h4"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {translate('Your_phone_number')}:
            </p>
            <i
              className="icon_mobile tertiary"
              aria-hidden="true"
              style={isDark ? styles.primary : styles.tertiary}
            />
            <p
              className="tertiary clear-margins fd-h4"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {this.props.params.phone}
            </p>
          </div>

          <div>{errorMessage}</div>

          <Form
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <FormGroup>
              <input
                aria-label={`${translate('Enter_sms_code')} ${translate(
                  'Sms_verify_screen_readers'
                )} ${translate('Your_phone_number')} ${this.props.params.phone}`}
                type="number"
                autoFocus={true}
                pattern="[0-9]*"
                onChange={this.handleSmsNumberChange}
                value={this.state.SmsCode}
                key="SmsInput"
                style={isDark ? styles.darkFormControl : styles.formControl}
                className="form-control"
                data-fd="sms-input"
              />

              {enableRememberMe && (
                <RememberMe
                  checked={rememberMe}
                  onChange={setRememberMe}
                  AppDesignScheme={AppDesignScheme}
                  isDark={isDark}
                />
              )}
            </FormGroup>

            <Grid container justify="center" spacing={3}>
              {/* <Grid item xs={12} sm={4}>
              <SubmitButton
                disabled={
                  !this.state.SmsCode || (this.state.SmsCode && this.state.SmsCode.length < 3)
                }
                loading={this.state.Submitting}
                onClick={this.handleSmsCodeSubmit}
                dataFd="sms-submit"
                variant="contained"
                size="large"
                fullWidth
                color="primary"
              >
                {buttonLabel}
              </SubmitButton>
            </Grid> */}

              <Hidden mdUp>
                {this.state.callRequested && (
                  <Grid item xs={12}>
                    <Typography color="textPrimary">
                      {translate('you_will_receive_call_soon')}
                    </Typography>
                  </Grid>
                )}
              </Hidden>

              <Grid item xs={12} sm={6} md={4}>
                <PhoneCallButton
                  translate={this.props.translate}
                  PhoneNumber={this.props.params.phone}
                  onSubmit={() => {
                    this.setState({
                      callRequested: true,
                      ErrorMessage: undefined
                    });
                  }}
                  onSuccess={() => {
                    Track('PhoneCall', 'Fallback Phone Call for SMS Code Successful');
                  }}
                  onError={(error) => {
                    if (error && error.message) {
                      this.setState({ ErrorMessage: error.message });
                    } else {
                      this.setState({
                        ErrorMessage: translate('There_was_an_error_requesting_a_phone_call')
                      });
                    }
                    Track('PhoneCall', 'Fallback Phone Call for SMS Code Failed');
                  }}
                >
                  {({ button }) => (
                    <Button
                      data-fd="request-call"
                      variant="contained"
                      size="large"
                      fullWidth
                      {...button}
                      disabled={button.disabled || this.state.Submitting}
                      // DESIGN BUG: there should be only one primary button on form and that's submit button
                      // color="primary"
                    />
                  )}
                </PhoneCallButton>
              </Grid>

              <Hidden mdDown>
                {this.state.callRequested && (
                  <Grid item xs={12}>
                    <Typography align="center" color="textPrimary">
                      {translate('you_will_receive_call_soon')}
                    </Typography>
                  </Grid>
                )}
              </Hidden>
            </Grid>
          </Form>
        </Container>
      </MainLayout>
    );
  }
}

const mapStateToProps = (state: Store.State): StateProps => {
  return {
    AppConfig: state.flipdish.AppConfig,
    DeviceId: state.flipdish.Config.DeviceId,
    AfterLogin: state.flipdish.AfterLogin,
    translate: getTranslate(state.locale),
    AppDesignScheme: state.appDesignScheme,
    isDark: state.flipdish.isDark,
    isFunctionalCookieConsentEnabled: selectors.isFunctionalCookieConsentEnabled(state),
    hasGdprCookieFeatureFlag: selectors.hasGdprCookieFeatureFlag(state),
    rememberMe: state.cookieConsent.rememberMe,
    enableRememberMe: selectors.enableRememberMe(state)
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    Login: () => {
      dispatch(Actions.LoadCustomer());
    },
    setRememberMe: (value: boolean) => dispatch(cookieConsentActions.setRememberMe(value))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Radium(SmsCode));
